import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Chip,
  Grid2,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableBody,
  TableHead,
  TableRow,
  Backdrop,
  LinearProgress
} from '@mui/material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { BulkUploadResponseDto } from '@thro-clients/polyas-connector-api';

import { AxiosError } from 'axios';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useBulkUploadJobStatus, useUploadVoterMutation } from 'api/hooks/polyas/polyas';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

interface VoterImportProps {
  electionId: string;
}

export function VoterImport({ electionId }: VoterImportProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [voters, setVoters] = useState<BulkUploadResponseDto | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { uploadVoter } = useUploadVoterMutation();
  const { data } = useBulkUploadJobStatus(electionId, jobId);
  const queryClient = useQueryClient();

  const uploadFile = async (file: File) => {
    try {
      const response = await uploadVoter.mutateAsync({ electionId: electionId, blob: file });
      setJobId(response.jobId);
    } catch (err) {
      if (err instanceof AxiosError) {
        dispatch(
          AlertActions.error(
            t(translations.common.alerts.failedFileUploadWithCause(), {
              cause: err.response?.data.message
            })
          )
        );
      }
    }
  };

  useEffect(() => {
    let timeoutId;
    try {
      if (!jobId) return;
      queryClient.invalidateQueries({ queryKey: ['bulkUploadStatus', electionId, jobId] });
      setIsProcessing(true);

      switch (data?.status) {
        case 'completed':
          setVoters(data.result);
          queryClient.invalidateQueries({ queryKey: ['electionVoters', electionId] });
          setIsProcessing(false);
          setJobId(undefined);
          break;
        case 'failed':
          dispatch(
            AlertActions.error(
              t(translations.pages.polyas.voter.import.bulkUploadFailedWithCause(), {
                cause: data.error
              })
            )
          );
          setIsProcessing(false);
          setJobId(undefined);
          break;
        case 'processing':
        case 'pending':
          timeoutId = setTimeout(() => {
            setRefreshTrigger(prev => prev + 1); // Erhöht den Zähler und triggert den Effekt neu
          }, 500);
          break;
        default:
          break;
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        dispatch(
          AlertActions.error(
            t(translations.common.alerts.failedFileUploadWithCause(), {
              cause: error.response?.data.message
            })
          )
        );
      }
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [data?.status, jobId, refreshTrigger, electionId]);

  const handleFile = useCallback(async (file: File) => {
    setIsProcessing(true);
    setFileName(file.name);
    try {
      await uploadFile(file);
    } catch (error) {
      setParseError(
        error instanceof Error
          ? error.message
          : t(translations.pages.polyas.voter.import.fileReadError())
      );
      setIsProcessing(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (
        file &&
        (file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.xlsx'))
      ) {
        handleFile(file);
      } else {
        setParseError(t(translations.pages.polyas.voter.import.onlyCsvAllowed()));
      }
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const progress = data?.progress ?? 0;
  const isCompleted = data?.status === 'completed';

  const getStepStatus = (stepIndex: number): 'completed' | 'active' | 'upcoming' => {
    if (isCompleted) return 'completed';

    switch (stepIndex) {
      case 0:
        return progress > 5 ? 'completed' : 'active';
      case 1:
        if (progress > 40) return 'completed';
        if (progress > 5) return 'active';
        return 'upcoming';
      case 2:
        if (progress > 70) return 'completed';
        if (progress > 40) return 'active';
        return 'upcoming';
      case 3:
        if (progress > 70) return 'active';
        return 'upcoming';
      default:
        return 'upcoming';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
      {/* Upload Loading Overlay */}
      <Backdrop
        open={isProcessing}
        sx={{
          position: 'absolute',
          zIndex: theme => theme.zIndex.drawer + 1,
          bgcolor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(8px)',
          flexDirection: 'column',
          gap: 2,
          borderRadius: 2,
          transition: 'all 0.3s ease'
        }}
      >
        <Paper
          elevation={4}
          sx={{
            p: 4,
            width: '90%',
            maxWidth: 420,
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.08)'
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: 'text.primary' }}>
              {t(translations.pages.polyas.voter.import.importing())}
            </Typography>
          </Box>

          {/* Steps List */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 1 }}>
            {[
              { label: t(translations.pages.polyas.voter.import.stepUpload()), index: 0 },
              { label: t(translations.pages.polyas.voter.import.stepSearch()), index: 1 },
              { label: t(translations.pages.polyas.voter.import.stepDuplicates()), index: 2 },
              { label: t(translations.pages.polyas.voter.import.stepSave()), index: 3 }
            ].map(step => {
              const status = getStepStatus(step.index);
              const isActive = status === 'active';
              const isDone = status === 'completed';

              return (
                <Box
                  key={step.index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: isActive ? 'action.selected' : 'transparent',
                    transition: 'all 0.2s ease',
                    opacity: status === 'upcoming' ? 0.5 : 1
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24
                    }}
                  >
                    {isDone ? (
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
                    ) : isActive ? (
                      <CircularProgress size={20} thickness={5} />
                    ) : (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          border: '2px solid',
                          borderColor: 'text.disabled',
                          bgcolor: 'transparent'
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body1"
                    fontWeight={isActive ? 600 : 400}
                    color={isActive ? 'text.primary' : isDone ? 'text.secondary' : 'text.disabled'}
                  >
                    {step.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ width: '100%', mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                mb: 1.5,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  transition: 'transform 0.4s ease'
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {Math.round(progress)}% {t(translations.pages.polyas.voter.import.completed())}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Backdrop>
      <Grid2
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          p: 4,
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'divider',
          bgcolor: isDragging ? 'primary.50' : 'background.default',
          borderRadius: 2,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover'
          }
        }}
        component="label"
      >
        <input
          type="file"
          accept=".xlsx,.csv"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        {isProcessing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={40} />
            <Typography>{t(translations.pages.polyas.voter.import.processingFile())}</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon
              sx={{ fontSize: 48, color: isDragging ? 'primary.main' : 'text.secondary' }}
            />
            <Typography variant="h6" color={isDragging ? 'primary.main' : 'text.primary'}>
              {t(translations.pages.polyas.voter.import.dragCsvHere())}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(translations.pages.polyas.voter.import.orClickToSelect())}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {t(translations.pages.polyas.voter.import.requiredColumns())}
            </Typography>
          </Box>
        )}
      </Grid2>

      {parseError && (
        <Alert severity="error" onClose={() => setParseError(null)}>
          <AlertTitle>{t(translations.pages.polyas.voter.import.errorProcessing())}</AlertTitle>
          {parseError}
        </Alert>
      )}

      {voters?.totalRows && voters.totalRows > 0 && (
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={500}>
                  {fileName}
                </Typography>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${voters.successCount} ${t(
                    translations.pages.polyas.voter.import.valid()
                  )}`}
                  color="success"
                  size="small"
                />
                {voters.failureCount > 0 && (
                  <Chip
                    icon={<ErrorIcon />}
                    label={`${voters.failureCount} ${t(
                      translations.pages.polyas.voter.import.invalid()
                    )}`}
                    color="error"
                    size="small"
                  />
                )}
              </Box>
            </Box>
          </Grid2>
          {/* ERROR USER */}
          <Grid2 size={12}>
            <Typography variant="h6">
              {t(translations.pages.polyas.voter.import.faultyUsers())}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(translations.pages.polyas.voter.import.couldNotBeImported())}
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={40}>
                      {t(translations.pages.polyas.voter.import.status())}
                    </TableCell>
                    <TableCell>{t(translations.pages.polyas.voter.import.email())}</TableCell>
                    <TableCell>{t(translations.pages.polyas.voter.import.firstName())}</TableCell>
                    <TableCell>{t(translations.pages.polyas.voter.import.lastName())}</TableCell>
                    <TableCell>{t(translations.pages.polyas.voter.import.note())}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {voters?.errors?.map((voter, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        bgcolor: 'error.50',
                        '&:hover': { bgcolor: 'error.100' }
                      }}
                    >
                      <TableCell>
                        <ErrorIcon color="error" fontSize="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {voter?.eligibleVoterUser?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {voter?.eligibleVoterUser?.firstName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {voter?.eligibleVoterUser?.lastName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {voter.errors && (
                          <Typography variant="caption" color="error">
                            {voter.errors.join(',')}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid2>

          {/* DUPLICATES */}
          {voters.duplicates.length > 0 && (
            <Grid2 size={12}>
              <Typography variant="h6">
                {t(translations.pages.polyas.voter.import.duplicateUsers())}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t(translations.pages.polyas.voter.import.duplicateUsersDescription())}
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width={40}>
                        {t(translations.pages.polyas.voter.import.status())}
                      </TableCell>
                      <TableCell>{t(translations.pages.polyas.voter.import.rowA())}</TableCell>
                      <TableCell>{t(translations.pages.polyas.voter.import.emailA())}</TableCell>
                      <TableCell>{t(translations.pages.polyas.voter.import.rowB())}</TableCell>
                      <TableCell>{t(translations.pages.polyas.voter.import.emailB())}</TableCell>
                      <TableCell>
                        {t(translations.pages.polyas.voter.import.similarity())}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {voters?.duplicates?.map((voter, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          bgcolor: 'inherit',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <TableCell>
                          <WarningIcon color="warning" fontSize="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {voter.rowA}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {voter.emailA}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {voter.rowB}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {voter.emailB}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {voter.distance}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid2>
          )}
        </Grid2>
      )}
    </Box>
  );
}
